'use client';

import { useEffect, type RefObject } from 'react';
import type { AgPublicEventType, GridApi } from 'ag-grid-community';

const VIEWPORT_SELECTORS = {
  body: '.ag-center-cols-viewport',
  header: '.ag-header-viewport',
} as const;

const GRID_LAYOUT_EVENTS = [
  'bodyScroll',
  'bodyScrollEnd',
  'gridSizeChanged',
  'columnResized',
  'displayedColumnsChanged',
  'virtualColumnsChanged',
] as const satisfies readonly AgPublicEventType[];

const resolveViewports = (container: HTMLElement) => {
  const bodyViewport = container.querySelector(VIEWPORT_SELECTORS.body);
  const headerViewport = container.querySelector(VIEWPORT_SELECTORS.header);

  if (
    !(bodyViewport instanceof HTMLElement) ||
    !(headerViewport instanceof HTMLElement)
  ) {
    return null;
  }

  return { bodyViewport, headerViewport };
};

export const syncHeaderScrollToBody = (container: HTMLElement) => {
  const viewports = resolveViewports(container);

  if (!viewports) {
    return;
  }

  const { bodyViewport, headerViewport } = viewports;

  if (headerViewport.scrollLeft !== bodyViewport.scrollLeft) {
    headerViewport.scrollLeft = bodyViewport.scrollLeft;
  }
};

export function useAgGridHeaderScrollSync({
  gridApi,
  gridContainerRef,
}: {
  gridApi: GridApi | null;
  gridContainerRef: RefObject<HTMLElement | null>;
}) {
  useEffect(() => {
    const container = gridContainerRef.current;

    if (!gridApi || !container) {
      return;
    }

    let animationFrameId: number | null = null;
    const syncNextFrame = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        syncHeaderScrollToBody(container);
      });
    };

    const scheduleSync = () => {
      syncHeaderScrollToBody(container);
      syncNextFrame();
    };

    const handleScroll = (event: Event) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      if (event.target.matches(VIEWPORT_SELECTORS.body)) {
        scheduleSync();
      } else if (event.target.matches(VIEWPORT_SELECTORS.header)) {
        syncNextFrame();
      }
    };

    const resizeObserver = new ResizeObserver(scheduleSync);

    container.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });
    resizeObserver.observe(container);
    GRID_LAYOUT_EVENTS.forEach((eventType) =>
      gridApi.addEventListener(eventType, scheduleSync),
    );
    scheduleSync();

    return () => {
      container.removeEventListener('scroll', handleScroll, { capture: true });
      resizeObserver.disconnect();

      if (!gridApi.isDestroyed()) {
        GRID_LAYOUT_EVENTS.forEach((eventType) =>
          gridApi.removeEventListener(eventType, scheduleSync),
        );
      }

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [gridApi, gridContainerRef]);
}
